import { paths } from '@/api/openapi/schema';
import { CANVAS_SIZE, FONT_FAMILIES } from '@/constant';
import { SlideComponentMCCreator } from '@/providers/editor/creators/SlideComponentMC.creator';
import { SlideComponentTextCreator } from '@/providers/editor/creators/SlideComponentText.creator';
import { SlideComponent, SlideComponentMC, SlideComponentMCAnswer, SlideComponentText } from '@/types/slide';

const TRADITIONAL_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZHKXinBiaoKai')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;
const SIMPLIFIED_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZKai-Z03S')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;

// Union type for all possible RC endpoint responses
export type RCResponse = paths['/experiments/generators/questions/gcse/rc']['post']['responses']['200']['content']['application/json'];

function getPassageTextContent(input: RCResponse): string {
    if (!input || input.question?.type !== 'mc' || !input.question.content) {
        return '';
    }
    if (!input || !input.question?.content) {
        return '';
    }

    if (input.question.content.panels && input.question.content.panels.length > 0) {
        let output = '';
        for (const panel of input.question.content.panels) {
            output += `${panel.content}`;
        }
        return output.trim();
    } else {
        return input.question.content.passage || '';
    }
}

const INSTRUCTION_ZHHK = '請閱讀以下短文，選擇唯一正確的答案：';
const INSTRUCTION_ZHCN = '请阅读以下短文，选择唯一正确的答案：';

export const generate = (input?: RCResponse): SlideComponent[][] => {
    // useTranslation hook cannot be used here, so hardcode the instruction text for now
    if (!input || input.question?.type !== 'mc' || !input.question.content) {
        return [];
    }

    const slides: SlideComponent[][] = [];

    for (const question of input.question.content.questions) {
        const components: SlideComponent[] = [];

        const margin = 16;
        let accumulatedHeight = margin;

        /**
         * Create the instruction text component
         */

        const instruction: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        instruction.content.text = input.question.content.language_variant === 'traditional' ? INSTRUCTION_ZHHK : INSTRUCTION_ZHCN;
        instruction.pos = { x: margin, y: accumulatedHeight };
        instruction.size = { width: CANVAS_SIZE.width - margin * 2, height: 50 };
        instruction.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        instruction.content.fontSize = 16;
        instruction.content.typeSettings.alignment = 'left';
        instruction.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(instruction);
        accumulatedHeight += instruction.size.height + margin;

        /**
         * Create the title text component
         */

        const title: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        title.content.text = input.question.content.title || '';
        title.pos = { x: margin, y: accumulatedHeight };
        title.size = { width: CANVAS_SIZE.width - margin * 2, height: 50 };
        title.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        title.content.fontSize = 16;
        title.content.typeSettings.alignment = 'center';
        title.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(title);
        accumulatedHeight += title.size.height + margin;

        /**
         * Create the intro text component
         */

        const intro: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        intro.content.text = input.question.content.intro || '';
        intro.pos = { x: margin, y: accumulatedHeight };
        intro.size = { width: CANVAS_SIZE.width - margin * 2, height: 50 };
        intro.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        intro.content.fontSize = 16;
        intro.content.typeSettings.alignment = 'left';
        intro.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(intro);
        accumulatedHeight += intro.size.height + margin;

        /**
         * Create the passage text component
         */
        const passageTextContent = getPassageTextContent(input);
        // TODO: I believe some error handling is needed here in case passageTextContent is empty

        const passage: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        passage.content.text = passageTextContent;
        passage.pos = { x: margin, y: accumulatedHeight };
        passage.size = { width: CANVAS_SIZE.width - margin * 2, height: 150 };
        passage.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        passage.content.fontSize = 16;
        passage.content.typeSettings.alignment = 'left';
        passage.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(passage);
        accumulatedHeight += passage.size.height + margin;

        /**
         * Create the SlideComponentMC component
         */

        /**
         * Create the question text component
         */
        const questionText: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
        questionText.content.text = question.question_text || '';
        questionText.pos = { x: margin, y: 440 };
        questionText.size = { width: CANVAS_SIZE.width - margin * 2, height: 50 };
        questionText.content.typeSettings.fontFamily = input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
        questionText.content.fontSize = 16;
        questionText.content.typeSettings.alignment = 'left';
        questionText.content.color = '#000000'; // Fallback to black if color is not defined
        components.push(questionText);

        const mc: SlideComponentMC = new SlideComponentMCCreator().create(crypto.randomUUID());
        mc.pos = { x: 0, y: 484 };
        mc.size = { width: CANVAS_SIZE.width, height: 284 };
        mc.content.answers = [];

        for (let i = 0; i < question.options.length; i++) {
            const option = question.options[i];
            /**
             * Create the answer text component
             */
            const answer: SlideComponentMCAnswer = {
                id: crypto.randomUUID(),
                type: 'text',
                pos: { x: 0, y: 0 },
                size: { width: 0, height: 0 },
                settings: {
                    backgroundColor: '#DADEE7',
                    isAnswer: question.correct_answer === i,
                },
                content: {
                    text: option || '',
                    jyutping: [],
                    fontSize: 16,
                    typeSettings: {
                        fontFamily: input.question.content.language_variant === 'traditional' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY,
                        formatting: [],
                        alignment: 'left',
                        isPhonetic: false,
                        borderRadius: 0,
                        backgroundColor: 'transparent',
                    },
                    color: '#000000',
                },
            };

            mc.content.answers.push(answer);
        }
        components.push(mc);
        slides.push(components);
    }

    return slides;
};
